import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ITenantScanBrowserTimeouts } from '../../../../shared/interfaces/tenant.interface';
import {
  $browserAufTimeouts,
  $browserSwaTimeouts,
  $browserTimeouts,
  BROWSER_AUF_TIMEOUTS,
  BROWSER_SWA_TIMEOUTS,
} from '../../../../shared/constants/browser';

enum $fields {
  modifyBrowserTimeouts = 'modifyBrowserTimeouts',
  timeoutsForm = 'browserTimeouts',
}

interface TimeoutsGroup {
  name: string;
  keys: [string, number][];
}

@Component({
  selector: 'app-scan-custom-browser-timeouts',
  templateUrl: './scan-custom-browser-timeouts.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScanCustomBrowserTimeoutsComponent implements OnInit, OnDestroy {
  @Input()
  public form: UntypedFormGroup;
  @Input()
  public formValidationRequest$: Observable<void>;
  public timeoutsForm: UntypedFormGroup;
  public $browserTimeouts: typeof $browserTimeouts = $browserTimeouts;
  public $browserSwaTimeouts: typeof $browserSwaTimeouts = $browserSwaTimeouts;
  public $browserAufTimeouts: typeof $browserAufTimeouts = $browserAufTimeouts;
  public modifyBrowserTimeoutsControlName: $fields = $fields.modifyBrowserTimeouts;
  public timeoutsFormName: $fields = $fields.timeoutsForm;

  private destroy$: Subject<void> = new Subject<void>();
  private subscriptions: Subscription[] = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
  ) {}

  private initializeTimeoutsForm(browserTimeouts: ITenantScanBrowserTimeouts): void {
    const timeoutsGroups: TimeoutsGroup[] = [
      {
        name: 'swaTimeouts',
        keys: Object.entries(BROWSER_SWA_TIMEOUTS),
      },
      {
        name: 'aufTimeouts',
        keys: Object.entries(BROWSER_AUF_TIMEOUTS),
      },
    ];

    this.timeoutsForm = this.formBuilder.group({});

    timeoutsGroups.forEach(({ name, keys }: TimeoutsGroup) => {
      const timeoutsControls = keys.reduce<Record<string, UntypedFormControl>>(
        (acc: Record<string, UntypedFormControl>, [key, value]: [string, number]) => {
          const defaultValue = browserTimeouts?.[name]?.[key] ?? value;
          const control = this.formBuilder.control(Number(defaultValue), {
            validators: [
              Validators.required,
              Validators.min(Math.floor(value / 3)),
              Validators.max(Math.floor(value * 3)),
              Validators.pattern('^[0-9]*$'), // Only allow numeric values
            ],
          });

          // Subscribe to value changes to update parent form
          const subscription = control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((newValue: string | number) => {
            if (control.dirty) {
              // Convert the value to a number and update the control
              const numericValue = Number(newValue);
              if (!isNaN(numericValue)) {
                control.setValue(numericValue, { emitEvent: false });
                control.markAsDirty();
                this.timeoutsForm.updateValueAndValidity({ emitEvent: true });
                this.cdr.markForCheck();
              } else {
                // If invalid, set to 0
                control.setValue(0, { emitEvent: false });
              }
            }
          });
          this.subscriptions.push(subscription);

          acc[key] = control;
          return acc;
        },
        {},
      );

      this.timeoutsForm.addControl(name, this.formBuilder.group(timeoutsControls));
    });

    this.form.addControl(this.timeoutsFormName, this.timeoutsForm);
  }

  @Input()
  public set modifyBrowserTimeouts(modifyBrowserTimeouts: boolean) {
    if (this.form) {
      const control = this.formBuilder.control(Boolean(modifyBrowserTimeouts));
      this.form.addControl(this.modifyBrowserTimeoutsControlName, control);
    }
  }

  @Input()
  public set browserTimeouts(browserTimeouts: ITenantScanBrowserTimeouts | undefined) {
    this.initializeTimeoutsForm(browserTimeouts);
  }

  public get modifyBrowserTimeouts(): AbstractControl | null {
    return this.form.get(this.modifyBrowserTimeoutsControlName);
  }

  ngOnInit(): void {
    // Subscribe to form validation requests
    const validationSubscription: Subscription | undefined = this.formValidationRequest$
      ?.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.timeoutsForm) {
          // Mark all controls as touched to show validation errors
          Object.values(this.timeoutsForm.controls).forEach((group: AbstractControl) => {
            if (group instanceof UntypedFormGroup) {
              Object.values(group.controls).forEach((control: AbstractControl) => {
                control.markAsTouched();
                control.updateValueAndValidity({ emitEvent: false });
              });
            }
          });
          this.timeoutsForm.updateValueAndValidity({ emitEvent: false });
          this.cdr.markForCheck();
        }
      });
    if (validationSubscription) {
      this.subscriptions.push(validationSubscription);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
    this.destroy$.next();
    this.destroy$.complete();
  }
}
